import CustomQuickRangeGroup from './CustomGroupChips';
import HazeSubsidyScheme from './HazeSubsidyScheme';
import { CloseIcon } from '@/assets/svg';
import CustomLoadingButton from '@/new-components/CustomLoadingButton';
import {
  Box,
  withStyles,
  InputLabel,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

const CustomInputLabel = withStyles({
  root: {
    color: '#666666',
    marginBottom: 8,
    fontWeight: 600,
  },
})(InputLabel);

const CustomTitle = withStyles({
  root: {
    fontWeight: 600,
    marginBottom: 20,
    color: '#333333',
  },
})(Typography);

const CustomTextField = withStyles({
  root: {
    fontSize: 20,

    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      height: 48,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: 0,
      paddingLeft: 5,
      fontSize: 16,
    },

    '& .MuiInputBase-inputMultiline': {
      '&.MuiInputBase-input': {
        marginLeft: 0,
      },
    },

    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
    '& .MuiInputBase-adornedStart': {
      '& .MuiTypography-root': {
        fontSize: 16,
        color: '#333333',
      },
      '& .MuiInputAdornment-positionStart': {
        marginRight: 0,
      },
      '& input': {
        marginLeft: 0,
      },
    },
    '& .MuiFormHelperText-root': {
      alignItems: 'center',
      display: 'flex',
      fontFamily: 'OpenSans',
      fontSize: 12,
      gap: 6,
      marginLeft: 2,

      '& .MuiSvgIcon-root': {
        fontSize: 'unset',
      },
    },
  },
})(TextField);

const SymptomsStep = ({
  selectedItems,
  setSelectedItems,
  handleSubmit,
  searchKey,
  setSearchKey,
}) => {
  const { symptoms } = useSelector((state) => state.teleConsultNow);
  const handleChangeFrom = (e, values) => {
    let newSearchKey = values.join(', ');

    // Use values directly instead of parsing from joined string
    // to avoid breaking symptom names that contain commas
    let newList = values
      .map((it) => {
        const trimmed = it.trim();
        if (!symptoms.includes(trimmed)) {
          return trimmed.replace(' ', '');
        }
        return trimmed;
      })
      .filter((it) => it);

    // Remove duplicates
    newList = newList.filter((item, pos) => {
      return newList.indexOf(item) === pos;
    });

    setSearchKey(newSearchKey);
    setSelectedItems(newList);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height={'100%'}
      px={2.5}
    >
      <Box maxWidth={600} textAlign="left" mt={2}>
        <CustomTitle>Why do you need a doctor?</CustomTitle>
        <CustomInputLabel>Symptoms</CustomInputLabel>

        <CustomTextField
          fullWidth
          variant="outlined"
          placeholder="Sore throat"
          value={searchKey}
          onChange={(e) => {
            const inputValue = e.target.value;
            setSearchKey(inputValue);

            if (!inputValue.trim()) {
              setSelectedItems([]);
              return;
            }

            // Check if the input value exactly matches a symptom in the list
            // If yes, treat it as a single symptom (don't split by comma)
            const trimmedInput = inputValue.trim();
            if (symptoms && symptoms.includes(trimmedInput)) {
              setSelectedItems([trimmedInput]);
              return;
            }

            // Otherwise, try to intelligently split by comma
            // First, check if any symptom in the list contains the input value
            // If a symptom contains commas and matches the input, treat as single symptom
            if (symptoms) {
              const matchingSymptom = symptoms.find(
                (symptom) =>
                  inputValue.includes(symptom) && symptom.includes(',')
              );
              if (matchingSymptom && inputValue.trim() === matchingSymptom) {
                setSelectedItems([matchingSymptom]);
                return;
              }
            }

            // Split by comma and validate each part against the symptoms list
            const parts = inputValue.split(',');
            let holdList = [];

            for (let i = 0; i < parts.length; i++) {
              const part = parts[i].trim();
              if (!part) continue;

              // Try to match this part with a symptom
              // If it matches exactly, add it
              if (symptoms && symptoms.includes(part)) {
                holdList.push(part);
              } else {
                // Try combining with following parts to see if it forms a valid symptom
                let combined = part;
                let matched = false;

                for (let j = i + 1; j < parts.length; j++) {
                  combined += ', ' + parts[j].trim();
                  const combinedTrimmed = combined.trim();

                  if (symptoms && symptoms.includes(combinedTrimmed)) {
                    holdList.push(combinedTrimmed);
                    i = j; // Skip the parts we've already processed
                    matched = true;
                    break;
                  }
                }

                // If no match found, add the part as-is (user might be typing)
                if (!matched) {
                  holdList.push(part);
                }
              }
            }

            // Remove duplicates
            holdList = holdList.filter((item, pos) => {
              return holdList.indexOf(item) === pos;
            });

            setSelectedItems(holdList);
          }}
          InputProps={{
            endAdornment: (
              <>
                <InputAdornment position="end">
                  <IconButton
                    style={{
                      visibility: !isEmpty(searchKey) ? 'visible' : 'hidden',
                    }}
                    onClick={() => {
                      setSearchKey('');
                      setSelectedItems([]);
                    }}
                  >
                    <CloseIcon
                      height={20}
                      width={20}
                      style={{
                        color: '#666666',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              </>
            ),
          }}
          style={{ marginBottom: 10 }}
        />

        {symptoms && (
          <>
            <CustomQuickRangeGroup
              value={selectedItems}
              handleChange={handleChangeFrom}
              listButtons={symptoms}
            />
          </>
        )}

        <HazeSubsidyScheme />
      </Box>

      <CustomLoadingButton
        onClick={handleSubmit}
        variant="contained"
        color="primary"
        fullWidth
        disabled={isEmpty(selectedItems)}
        style={{ marginTop: 24, width: '100%' }}
      >
        Call now
      </CustomLoadingButton>
    </Box>
  );
};

export default SymptomsStep;
